import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { ChakraProvider } from '@chakra-ui/react';

import App from './App';
import * as serviceWorker from './serviceWorker';
import HomePage from './pages/home';
import Error404 from './pages/errors/404';
import DistributorsPage from './pages/distributors';
import EquipmentsGuidePage from './pages/equipments-guide';
import EquipmentsPage from './pages/equipments';
import UsersPage from './pages/users';
import MapPage from './pages/map';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet/dist/leaflet.css';
import FilePage from './pages/file';
import AgreemensPage from './pages/agreements';
import EquipmentsUnitsPage from './pages/equipments-units';
import UniversePage from './pages/universe';
import EquipmentsStandsPage from './pages/equipments-stands';
import EquipmentsMapPage from './pages/equipments-map';
import EquipmentsStandModelsGuidePage from './pages/equipments-stand-models-guide';
import HelpPage from './pages/help';
import OfflinePage from './pages/offline';
import ServicePage from './pages/service';
import EquipmentsRepairPage from './pages/equipments-repair';
import { CustomerListDomain } from './pages/customer-list';
import { Wiki } from './pages/wiki';
import { MailingListsDomain } from './pages/mailing';
import { ProfileContextProvider } from './contexts/profile';
import { chakraTheme } from './styles/theme';
import { DeliveriesTable } from './pages/socket/deliveries/table';
import { SessionsTable } from './pages/socket/sessions/table';
import ReportsPage from './pages/reports';
import { OrgStructureDomain } from './pages/org-structure';
import { DMPTypesTable } from './pages/stands/dmp-types/table';
import { DMPGroupsTable } from './pages/stands/dmp-groups/table';
import { SupportChat } from './pages/support-chat';
import { WarehouseTypesTable } from './pages/distributors-page/warehouse-types/table';
import { WarehousesTable } from './pages/distributors-page/warehouses/table';
import { CategoryShelvesTable } from './pages/category-shelves/table';
import { SkuCategoriesTable } from './pages/sku/categories/table';
import { SkuSubcategoriesTable } from './pages/sku/subcategories/table';
import { SkuUnitsTable } from './pages/sku/units/table';
import { SkuManagement } from './pages/sku/management';
import { OrdersTable } from './pages/orders/list/table';
import { OrderAttributesTable } from './pages/orders/attributes/table';
import { OrderStatusesTable } from './pages/orders/statuses/table';


const root = createRoot(document.getElementById('root'));

root.render(
  <BrowserRouter>
    <ProfileContextProvider>
      <ChakraProvider theme={chakraTheme}>
        <App>
          <Switch>
            <Route exact path="/" component={HomePage} />
            <Route
              exact
              path={[
                '/distributors/edit/:id',
                '/distributors/create',
                '/distributors',
              ]}
              component={DistributorsPage}
            />
            <Route
              exact
              path={['/sales-visiting/offline']}
              component={OfflinePage}
            />
            <Route
              exact
              path={['/equipments-guide/edit/:id', '/equipments-guide/']}
              component={EquipmentsGuidePage}
            />
            <Route
              exact
              path={[
                '/equipments-stand-model-guide/edit/:id',
                '/equipments-stand-model-guide',
              ]}
              component={EquipmentsStandModelsGuidePage}
            />
            <Route
              exact
              path="/equipments-units"
              component={EquipmentsUnitsPage}
            />
            <Route
              exact
              path={['/equipments-stands/edit/:id', '/equipments-stands']}
              component={EquipmentsStandsPage}
            />
            <Route
              exact
              path="/equipments-dmp-types"
              component={DMPTypesTable}
            />
            <Route
              exact
              path="/equipments-dmp-groups"
              component={DMPGroupsTable}
            />
            <Route
              exact
              path="/warehouse-types"
              component={WarehouseTypesTable}
            />
            <Route
              exact
              path="/category_shelves"
              component={CategoryShelvesTable}
            />
            <Route
              exact
              path="/warehouses"
              component={WarehousesTable}
            />
            <Route
              exact
              path="/sku/management"
              component={SkuManagement}
            />
            <Route
              exact
              path="/sku/categories"
              component={SkuCategoriesTable}
            />
            <Route
              exact
              path="/sku/subcategories"
              component={SkuSubcategoriesTable}
            />
            <Route
              exact
              path="/sku/units"
              component={SkuUnitsTable}
            />
            <Route
              exact
              path="/orders"
              component={OrdersTable}
            />
            <Route
              exact
              path="/orders/attributes"
              component={OrderAttributesTable}
            />
            <Route
              exact
              path="/orders/statuses"
              component={OrderStatusesTable}
            />
            <Route
              exact
              path={['/equipments-repairs/edit/:id', '/equipments-repairs']}
              component={EquipmentsRepairPage}
            />
            <Route
              exact
              path={[
                '/equipments/create',
                '/equipments/transactions',
                '/equipments/locations',
                '/equipments',
                '/equipments-check',
                '/equipments-check-new',
                '/equipments/imports',
                '/equipments/paid-installations',
              ]}
              component={EquipmentsPage}
            />
            <Route
              exact
              path={['/equipments-map/:bbox', '/equipments-map']}
              component={EquipmentsMapPage}
            />
            <Route
              exact
              path={[
                '/agreements/edit/:id',
                '/agreements/create',
                '/agreements/',
              ]}
              component={AgreemensPage}
            />
            <Route exact path="/reports/:item" component={ReportsPage} />
            <Route
              exact
              path={[
                '/users/edit',
                '/users/:id(\\d+)',
                '/users/create',
                '/users/',
              ]}
              component={UsersPage}
            />
            <Route exact path="/smartsale/map" component={MapPage} />
            <Route
              exact
              path={['/smartsale/universe/:bbox', '/smartsale/universe']}
              component={UniversePage}
            />
            <Route
              exact
              path={[
                '/logs',
                '/history',
                '/vars',
                '/deferred-delivery',
                '/sessions',
              ]}
              component={ServicePage}
            />
            <Route exact path="/file/:id" component={FilePage} />
            <Route
              exact
              path={['/help/:item', '/help/:item/:item']}
              component={HelpPage}
            />
            <Route
              exact
              path={['/acl/:i', '/acl/:i/:i', '/acl/:i/:i/:i']}
              component={CustomerListDomain}
            />
            <Route
              exact
              path={['/org-structure/:i', '/org-structure/:i/:i', '/org-structure/:i/:i/:i']}
              component={OrgStructureDomain}
            />

            <Route exact path="/wiki" component={Wiki} />
            <Route exact path="/mailing-lists" component={MailingListsDomain} />
            <Route exact path="/deliveries/list" component={DeliveriesTable} />
            <Route exact path="/sessions/list" component={SessionsTable} />
            <Route exact path="/support-chat" component={SupportChat} />
            <Route path="*" component={Error404} />
          </Switch>
        </App>
      </ChakraProvider>
    </ProfileContextProvider>
  </BrowserRouter>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
